import { Component, ViewEncapsulation, OnInit, AfterViewInit } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { InvoiceService } from 'src/app/services/invoice/invoice.service';
import { UserService } from 'src/app/services/user/user.service';
import { ClientService } from 'src/app/services/user/client.service';
import { GeneralService } from 'src/app/services/general/general.service';
import { Invoice } from 'src/app/shared/models/invoice.interface';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { USER_TYPES } from 'src/app/config/system.config';
import Swal from 'sweetalert2';
import { HelperService } from 'src/app/services/helper/helper.service';
declare var KTMenu: any;

// Define filter interface
interface SalesFilters {
  salesPerson?: any;
  customer?: any;
  date_from?: string;
  date_to?: string;
  status?: string[];
}

@Component({
  selector: 'app-sales',
  templateUrl: './sales.component.html',
  styleUrls: ['./sales.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class SalesComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  invoices: Invoice[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  currentDate:any;

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;

  // Filter properties
  showFilters: boolean = false;
  filters: SalesFilters = {};

  // Customer search properties
  customerSearchTerm: string = '';
  selectedCustomer: any = null;
  customerSearchResults: any[] = [];
  showCustomerDropdown: boolean = false;
  isCustomerSearching: boolean = false;
  private customerSearchSubject = new Subject<string>();

  // Sales person search properties
  salesPersonSearchTerm: string = '';
  selectedSalesPerson: any = null;
  salesPersonSearchResults: any[] = [];
  showSalesPersonDropdown: boolean = false;
  isSalesPersonSearching: boolean = false;
  private salesPersonSearchSubject = new Subject<string>();

  // Status checkboxes
  statusFilters = {
    inJapan: false,
    inTransit: false,
    arrived: false,
    cleared: false,
    all: false
  };

  // Search subject for debouncing
  private searchSubject = new Subject<string>();

  // Excel export property
  isExportingExcel: boolean = false;

  constructor(
    private invoiceService: InvoiceService,
    private userService: UserService,
    private clientService: ClientService,
    private generalService: GeneralService,
    private helperService: HelperService
  ) {
    super();
    this.setupSearchDebounce();
    this.setupCustomerSearchDebounce();
    this.setupSalesPersonSearchDebounce();
  }

  ngOnInit(): void {
    this.loadInvoices();
  }

  ngAfterViewInit(): void {
    // Initialize KTMenu instances for dropdown menus
    this.initializeMenus();
  }

  setupSearchDebounce(): void {
    this.subscriptions.push(
      this.searchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(searchTerm => {
        this.loadInvoices(1, searchTerm);
      })
    );
  }

  setupCustomerSearchDebounce(): void {
    this.subscriptions.push(
      this.customerSearchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(searchTerm => {
        this.searchCustomers(searchTerm);
      })
    );
  }

  setupSalesPersonSearchDebounce(): void {
    this.subscriptions.push(
      this.salesPersonSearchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(searchTerm => {
        this.searchSalesPersons(searchTerm);
      })
    );
  }

  initializeMenus(): void {
    setTimeout(() => {
      KTMenu.createInstances();
    }, 100);
  }

  refreshMenus(): void {
    setTimeout(() => {
      KTMenu.createInstances();
    }, 100);
  }

  loadInvoices(page: number = this.currentPage, search: string = this.searchTerm, filters: any = {}): void {
    this.isLoading = true;
    this.currentPage = page;

    // Build filters for API call
    const apiFilters = Object.keys(filters).length > 0 ? filters : {};

    this.subscriptions.push(
      this.invoiceService.GetData(page.toString(), search, apiFilters).subscribe({
        next: (response: PaginatedResponse<Invoice>) => {
          if (response.status && response.data) {
            this.invoices = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
            this.currentDate = response.data.pagination.currentDate;
            this.refreshMenus();
          } else {
            this.invoices = [];
            this.resetPagination();
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading invoices:', error);
          this.invoices = [];
          this.resetPagination();
          this.isLoading = false;
        }
      })
    );
  }

  resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  onSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
    this.searchSubject.next(this.searchTerm);
  }

  onPageChange(page: number): void {
    this.loadInvoices(page);
  }

  // Filter methods
  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  hasActiveFilters(): boolean {
    const hasBasicFilters = Object.values(this.filters).some(value => value !== '' && value !== null && value !== undefined);
    const hasStatusFilters = Object.values(this.statusFilters).some(value => value === true);
    return hasBasicFilters || hasStatusFilters;
  }

  getActiveFiltersCount(): number {
    const basicFiltersCount = Object.values(this.filters).filter(value => value !== '' && value !== null && value !== undefined).length;
    const statusFiltersCount = Object.values(this.statusFilters).filter(value => value === true).length;
    return basicFiltersCount + statusFiltersCount;
  }

  clearFilters(): void {
    this.filters = {};
    // Clear customer search
    this.clearCustomerSearch();
    // Clear sales person search
    this.clearSalesPersonSearch();
    // Clear status filters
    this.statusFilters = {
      inJapan: false,
      inTransit: false,
      arrived: false,
      cleared: false,
      all: false
    };
    this.applyFilters();
  }

  applyFilters(): void {
    // Build filters for API call
    const apiFilters: any = {};

    // Add customer filter
    if (this.filters.customer) {
      apiFilters.customer_id = this.filters.customer._id;
    }

    // Add sales person filter
    if (this.filters.salesPerson) {
      apiFilters.sales_person_id = this.filters.salesPerson._id;
    }

    // Add date range filters
    if (this.filters.date_from) {
      apiFilters.date_from = this.filters.date_from;
    }
    if (this.filters.date_to) {
      apiFilters.date_to = this.filters.date_to;
    }

    // Add status filters
    const selectedStatuses = [];
    if (this.statusFilters.inJapan) selectedStatuses.push('in_japan');
    if (this.statusFilters.inTransit) selectedStatuses.push('in_transit');
    if (this.statusFilters.arrived) selectedStatuses.push('arrived');
    if (this.statusFilters.cleared) selectedStatuses.push('cleared');

    if (selectedStatuses.length > 0) {
      apiFilters.vehicle_status = selectedStatuses.join(',');
    }

    this.loadInvoices(1, this.searchTerm, apiFilters);
  }

  // Excel Export method
  exportExcel(): void {
    this.isExportingExcel = true;

    const filtersObj = this.buildFilters();
    filtersObj.type = 'invoice';
    filtersObj.excel_name = 'Sales Report';
    filtersObj.invoice_type = 'report';

    this.subscriptions.push(
      this.generalService.generateExcelPost(filtersObj).subscribe({
        next: (response: any) => {
          this.isExportingExcel = false;
          console.log('Excel export response:', response);

          if (response.status && response.data && response.data.url) {
            // Open the Excel file URL in a new tab
            window.open(response.data.url, '_blank');
          } else {
            // Show error message if no URL is returned
            Swal.fire({
              text: 'Failed to generate Excel file. No URL received.',
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn fw-bold btn-primary",
              }
            });
          }
        },
        error: (error: any) => {
          this.isExportingExcel = false;
          console.error('Error exporting Excel:', error);
          Swal.fire({
            text: 'An error occurred while exporting Excel file.',
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-primary",
            }
          });
        }
      })
    );
  }

  private buildFilters(): Record<string, any> {
    const filters: Record<string, any> = {};

    // Add customer filter
    if (this.filters.customer) {
      filters.customer_id = this.filters.customer._id;
    }

    // Add sales person filter
    if (this.filters.salesPerson) {
      filters.sales_person_id = this.filters.salesPerson._id;
    }

    // Add date range filters
    if (this.filters.date_from) {
      filters.date_from = this.filters.date_from;
    }
    if (this.filters.date_to) {
      filters.date_to = this.filters.date_to;
    }

    // Add status filters
    const selectedStatuses = [];
    if (this.statusFilters.inJapan) selectedStatuses.push('in_japan');
    if (this.statusFilters.inTransit) selectedStatuses.push('in_transit');
    if (this.statusFilters.arrived) selectedStatuses.push('arrived');
    if (this.statusFilters.cleared) selectedStatuses.push('cleared');

    if (selectedStatuses.length > 0) {
      filters.vehicle_status = selectedStatuses.join(',');
    }

    return filters;
  }

  // Customer search methods
  onCustomerSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.customerSearchTerm = target.value;
    this.selectedCustomer = null;
    this.customerSearchSubject.next(this.customerSearchTerm);
    this.showCustomerDropdown = true;
  }

  onCustomerInputFocus(): void {
    if (this.customerSearchResults.length > 0) {
      this.showCustomerDropdown = true;
    }
  }

  onCustomerInputBlur(): void {
    setTimeout(() => {
      this.showCustomerDropdown = false;
    }, 200);
  }

  searchCustomers(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.customerSearchResults = [];
      this.showCustomerDropdown = false;
      return;
    }

    this.isCustomerSearching = true;
    this.subscriptions.push(
      this.clientService.GetData('1', searchTerm, {
        role_type: USER_TYPES.CLIENT
      }).subscribe({
        next: (response: any) => {
          this.isCustomerSearching = false;
          if (response.status && response.data && response.data.data) {
            this.customerSearchResults = response.data.data;
            this.showCustomerDropdown = this.customerSearchResults.length > 0;
          } else {
            this.customerSearchResults = [];
            this.showCustomerDropdown = false;
          }
        },
        error: (error) => {
          this.isCustomerSearching = false;
          console.error('Error searching customers:', error);
          this.customerSearchResults = [];
          this.showCustomerDropdown = false;
        }
      })
    );
  }

  selectCustomer(customer: any): void {
    this.selectedCustomer = customer;
    this.customerSearchTerm = this.getCustomerDisplayName(customer);
    this.showCustomerDropdown = false;
    this.customerSearchResults = [];
    this.filters.customer = customer;
  }

  clearCustomerSearch(): void {
    this.customerSearchTerm = '';
    this.selectedCustomer = null;
    this.showCustomerDropdown = false;
    this.customerSearchResults = [];
    this.filters.customer = null;
  }

  getCustomerDisplayName(customer: any): string {
    if (customer.name && customer.company) {
      return `${customer.name}, ${customer.company}`;
    }
    return customer.name || customer.email || 'Unknown Customer';
  }

  // Sales person search methods
  onSalesPersonSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.salesPersonSearchTerm = target.value;
    this.selectedSalesPerson = null;
    this.salesPersonSearchSubject.next(this.salesPersonSearchTerm);
    this.showSalesPersonDropdown = true;
  }

  onSalesPersonInputFocus(): void {
    if (this.salesPersonSearchResults.length > 0) {
      this.showSalesPersonDropdown = true;
    }
  }

  onSalesPersonInputBlur(): void {
    setTimeout(() => {
      this.showSalesPersonDropdown = false;
    }, 200);
  }

  searchSalesPersons(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.salesPersonSearchResults = [];
      this.showSalesPersonDropdown = false;
      return;
    }

    this.isSalesPersonSearching = true;
    this.subscriptions.push(
      this.userService.GetData('1', searchTerm, { is_primary_agent: true }).subscribe({
        next: (response: any) => {
          this.isSalesPersonSearching = false;
          if (response.status && response.data && response.data.data) {
            this.salesPersonSearchResults = response.data.data;
            this.showSalesPersonDropdown = this.salesPersonSearchResults.length > 0;
          } else {
            this.salesPersonSearchResults = [];
            this.showSalesPersonDropdown = false;
          }
        },
        error: (error: any) => {
          this.isSalesPersonSearching = false;
          console.error('Error searching sales persons:', error);
          this.salesPersonSearchResults = [];
          this.showSalesPersonDropdown = false;
        }
      })
    );
  }

  selectSalesPerson(salesPerson: any): void {
    this.selectedSalesPerson = salesPerson;
    this.salesPersonSearchTerm = this.getSalesPersonDisplayName(salesPerson);
    this.showSalesPersonDropdown = false;
    this.salesPersonSearchResults = [];
    this.filters.salesPerson = salesPerson;
  }

  clearSalesPersonSearch(): void {
    this.salesPersonSearchTerm = '';
    this.selectedSalesPerson = null;
    this.showSalesPersonDropdown = false;
    this.salesPersonSearchResults = [];
    this.filters.salesPerson = null;
  }

  getSalesPersonDisplayName(salesPerson: any): string {
    if (salesPerson.title && salesPerson.name) {
      return `${salesPerson.title} ${salesPerson.name}`;
    }
    return salesPerson.name || salesPerson.email || 'Unknown Sales Person';
  }

  // Status filter methods
  onStatusFilterChange(status: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;

    if (status === 'all') {
      // If "All" is checked/unchecked, update all other checkboxes
      this.statusFilters.all = isChecked;
      this.statusFilters.inJapan = isChecked;
      this.statusFilters.inTransit = isChecked;
      this.statusFilters.arrived = isChecked;
      this.statusFilters.cleared = isChecked;
    } else {
      // Update individual status
      (this.statusFilters as any)[status] = isChecked;

      // If any individual status is unchecked, uncheck "All"
      if (!isChecked) {
        this.statusFilters.all = false;
      } else {
        // If all individual statuses are checked, check "All"
        const allIndividualChecked = this.statusFilters.inJapan &&
                                   this.statusFilters.inTransit &&
                                   this.statusFilters.arrived &&
                                   this.statusFilters.cleared;
        this.statusFilters.all = allIndividualChecked;
      }
    }
  }

  // Utility methods
  formatDate(date: string | Date | null | undefined): string {
    if (!date) return '-';
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleDateString('en-CA'); // YYYY-MM-DD format
    } catch (error) {
      return '-';
    }
  }

  formatCurrency(amount: number, currency: string = 'JPY'): string {
    if (!amount && amount !== 0) return '-';
    const symbol = currency === 'USD' ? '$' : '¥';
    return `${symbol} ${amount.toLocaleString()}`;
  }

  calculatePaidPercentage(paid: number, total: number): string {
    if (!total || total === 0) return '0%';
    const percentage = (paid / total) * 100;
    return `${percentage.toFixed(1)}%`;
  }

  // Track by function for ngFor performance
  trackByInvoiceId(index: number, invoice: Invoice): string {
    return invoice._id || index.toString();
  }

  getStatus(invoice:Invoice):string{

    return this.helperService.getInvoiceStatus(invoice,this.currentDate);

  }


  getRowColor(invoice:Invoice):string{

    if(invoice.ship && invoice.ship.sailing_date < this.currentDate && invoice.ship.arrival_date > this.currentDate && !invoice.is_paid){
      return ""; // In Transit -> No Color
    }

    if(invoice.ship && invoice.ship.sailing_date < this.currentDate && invoice.ship.arrival_date < this.currentDate && !invoice.is_paid){
      return "table-warning"; // Arrived -> Light orange (warning)
    }

    if((!invoice.ship || (invoice.ship && invoice.ship.sailing_date < this.currentDate)) && !invoice.is_paid){
      return ""; // In Japan -> No Color
    }

    if(invoice.ship && invoice.ship.sailing_date < this.currentDate && invoice.ship.arrival_date < this.currentDate && invoice.is_paid){
      return "table-success"; // Cleared -> Green color (success)
    }



    return ""; // No Status -> No Color
  }

  getAge(invoice:Invoice):string{

    return this.helperService.getAge(invoice,this.currentDate);
  }

}
